// ===== 3. AI Recommendation Accordion Component =====
// src/components/AIRecommendationAccordion.tsx

import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import SmartToyIcon from '@mui/icons-material/SmartToy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import { styled } from '@mui/material/styles';
import { AIRecommendationResponse, SubcodeRecommendation } from './aiSubcodeRecommendationService';

const RecommendationCard = styled(Card)(({ theme }) => ({
  transition: 'all 0.2s ease-in-out',
  cursor: 'pointer',
  height: '100%',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4]
  },
  '&.required': {
    borderLeft: `4px solid ${theme.palette.error.main}`,
    backgroundColor: '#fff5f5'
  },
  '&.optional': {
    borderLeft: `4px solid ${theme.palette.warning.main}`,
    backgroundColor: '#fffbf0'
  },
  '&.selected': {
    borderLeft: `4px solid ${theme.palette.success.main}`,
    backgroundColor: '#f0fff4'
  }
}));

interface SubcodeRecommendationCardProps {
  recommendation: SubcodeRecommendation;
  isSelected: boolean;
  onToggle: (isSelected: boolean) => void;
  isMobile: boolean;
}

const SubcodeRecommendationCard: React.FC<SubcodeRecommendationCardProps> = ({
  recommendation,
  isSelected,
  onToggle,
  isMobile
}) => {
  const { subcodeType, required, confidence, reason, priority } = recommendation;

  return (
    <RecommendationCard
      className={`${required ? 'required' : 'optional'} ${isSelected ? 'selected' : ''}`}
      onClick={() => onToggle(!isSelected)}
    >
      <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Typography variant={isMobile ? "body1" : "subtitle1"} fontWeight="bold">
            {subcodeType}
          </Typography>
          <Box display="flex" gap={0.5} flexWrap="wrap">
            <Chip
              label={required ? 'Required' : 'Optional'}
              size="small"
              color={required ? 'error' : 'warning'}
              variant="filled"
            />
            <Chip
              label={`${Math.round(confidence * 100)}%`}
              size="small"
              variant="outlined"
            />
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" mb={2} sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
          {reason}
        </Typography>

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" fontWeight="bold" color="text.secondary">
            Priority: {priority}
          </Typography>
          <Button
            variant={isSelected ? 'contained' : 'outlined'}
            size="small"
            startIcon={isSelected ? <CheckCircleIcon /> : null}
            color={required ? 'error' : 'primary'}
            sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
          >
            {isSelected ? 'Selected' : 'Select'}
          </Button>
        </Box>
      </CardContent>
    </RecommendationCard>
  );
};

interface AIRecommendationContentProps {
  recommendations: AIRecommendationResponse | null;
  loading: boolean;
  error: string | null;
  selectedSubcodes: string[];
  onSubcodeToggle: (subcodeType: string, isSelected: boolean) => void;
  isMobile: boolean;
}

const AIRecommendationContent: React.FC<AIRecommendationContentProps> = ({
  recommendations,
  loading,
  error,
  selectedSubcodes,
  onSubcodeToggle,
  isMobile
}) => {
  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column" gap={2} py={4}>
        <CircularProgress size={40} />
        <Typography variant="body1" color="text.secondary" textAlign="center">
          Analyzing your project requirements...
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          This may take a few seconds
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        <Typography variant="h6">⚠️ AI Analysis Unavailable</Typography>
        <Typography variant="body2">
          {error}. You can still manually select subcodes below.
        </Typography>
      </Alert>
    );
  }

  if (!recommendations) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="h6" gutterBottom color="text.secondary">
          📝 Ready for Analysis
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Complete the General Information and Property Details sections to get AI-powered subcode recommendations.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body1" gutterBottom>
        Based on your project details, here are the recommended subcodes:
      </Typography>

      {recommendations.warningMessages?.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {recommendations.warningMessages.map((msg, idx) => (
            <Typography key={idx} variant="body2">
              <WarningIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
              {msg}
            </Typography>
          ))}
        </Alert>
      )}

      <Grid container spacing={isMobile ? 1 : 2} mb={3}>
        {recommendations.recommendations.map((recommendation) => (
          <Grid item xs={12} sm={6} lg={4} key={recommendation.subcodeType}>
            <SubcodeRecommendationCard
              recommendation={recommendation}
              isSelected={selectedSubcodes.some(selected =>
                selected.toLowerCase() === recommendation.subcodeType.toLowerCase() ||
                selected.toLowerCase().replace(/\s+/g, '') === recommendation.subcodeType.toLowerCase().replace(/\s+/g, '')
              )}
              onToggle={(isSelected) => onSubcodeToggle(recommendation.subcodeType, isSelected)}
              isMobile={isMobile}
            />
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant="h6">
          Selected Subcodes: {selectedSubcodes.length}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {selectedSubcodes.filter(selected =>
            recommendations.recommendations.some(rec =>
              rec.subcodeType.toLowerCase() === selected.toLowerCase() ||
              rec.subcodeType.toLowerCase().replace(/\s+/g, '') === selected.toLowerCase().replace(/\s+/g, '')
            )
          ).length} of {recommendations.recommendations.length} recommendations selected
        </Typography>
      </Box>

      {/* {recommendations.infoMessages?.length > 0 && (
        <Box mt={2}>
          {recommendations.infoMessages.map((msg, idx) => (
            <Typography key={idx} variant="body2" color="text.secondary">
              <InfoIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
              {msg}
            </Typography>
          ))}
        </Box>
      )} */}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
        💡 Recommendations generated based on your project details.
        You can also select additional subcodes below if needed for your specific project.
      </Typography>
    </Box>
  );
};

interface AIRecommendationAccordionProps {
  recommendations: AIRecommendationResponse | null;
  loading: boolean;
  error: string | null;
  selectedSubcodes: string[];
  onSubcodeToggle: (subcodeType: string, isSelected: boolean) => void;
}

const AIRecommendationAccordion: React.FC<AIRecommendationAccordionProps> = ({
  recommendations,
  loading,
  error,
  selectedSubcodes,
  onSubcodeToggle,
}) => {
  const [expanded, setExpanded] = useState<boolean>(false); // Initially closed
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleAccordionChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);
  };

  const getStatusText = () => {
    if (loading) return 'Analyzing...';
    if (error) return 'Analysis Failed';
    if (!recommendations) return 'Waiting for Data';
    return `${recommendations.recommendations.length} Recommendations`;
  };

  const getStatusIcon = () => {
    if (loading) return <CircularProgress size={20} />;
    if (error) return '⚠️';
    if (!recommendations) return '📝';
    return '📝 ';
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={handleAccordionChange}
      sx={{
        mb: 3,
        '&:before': { display: 'none' },
        boxShadow: theme.shadows[2]
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: theme.palette.primary.light,
          color: theme.palette.primary.contrastText,
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            gap: 2
          }
        }}
      >
        <Box display="flex" alignItems="center" gap={2} flex={1}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getStatusIcon()}
            Subcode Recommendations
          </Typography>

          <Box sx={{ ml: 'auto', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={getStatusText()}
              size="small"
              variant="outlined"
              sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'inherit' }}
            />
            {recommendations && (
              <Chip
                label={`${Math.round(recommendations.overallConfidence * 100)}% Confidence`}
                size="small"
                variant="outlined"
                sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'inherit' }}
              />
            )}
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: { xs: 2, sm: 3 } }}>
        <AIRecommendationContent
          recommendations={recommendations}
          loading={loading}
          error={error}
          selectedSubcodes={selectedSubcodes}
          onSubcodeToggle={onSubcodeToggle}
          isMobile={isMobile}
        />
      </AccordionDetails>
    </Accordion>
  );
};

export default AIRecommendationAccordion;